/**
 * Messwertanzeige mit CSV-Ausgabe (W12).
 *
 * Ein Diagramm je Kanal statt aller Kurven uebereinander: die Groessen haben
 * verschiedene Einheiten und Wertebereiche - Volt neben rad/s neben Metern.
 * Uebereinandergelegt waere entweder die eine Kurve ein Strich am Rand oder
 * die andere nicht mehr aufgeloest.
 *
 * Fehlende Messwerte erscheinen als Luecke in der Linie. Eine durchgezogene
 * Linie darueber hinweg wuerde einen Verlauf behaupten, den niemand gemessen
 * hat (G08).
 */

import { Button, Text } from "@microbit/ui";
import { useCallback, useMemo, useState } from "react";
import {
  RiBox3Line,
  RiDeleteBinLine,
  RiDownloadLine,
  RiPauseLine,
  RiPlayLine,
  RiZoomInLine,
  RiZoomOutLine,
} from "react-icons/ri";
import { FormattedMessage, useIntl } from "react-intl";
import { HStack, VStack } from "styled-system/jsx";
import TelemetryChart, { Legend } from "./TelemetryChart";
import OrientationView from "./OrientationView";
import { hasOrientation, ORIENTATION_CHANNELS } from "./orientation";
import { toCsv } from "./series";
import RemoteControls from "./RemoteControls";
import { useTelemetry } from "./telemetry-hooks";

/** Waehlbare Hoehen des Diagramms in Bildpunkten. */
const HEIGHTS = [110, 170, 250, 360];
const DEFAULT_HEIGHT_INDEX = 1;

const TelemetryPanel = () => {
  const { series, revision, recording, setRecording, clear } = useTelemetry();
  const intl = useIntl();
  const [heightIndex, setHeightIndex] = useState(DEFAULT_HEIGHT_INDEX);
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  // Eingeschaltet, sobald eine Lage kommt: wer send_orientation() aufruft, will
  // sie sehen. Abschaltbar, weil die Ansicht Platz braucht, den bei vielen
  // Kanaelen das Diagramm besser nutzt.
  const [showOrientation, setShowOrientation] = useState(true);
  const height = HEIGHTS[heightIndex];
  const toggle = useCallback((name: string) => {
    setHidden((bisher) => {
      const neu = new Set(bisher);
      if (neu.has(name)) {
        neu.delete(name);
      } else {
        neu.add(name);
      }
      return neu;
    });
  }, []);

  // revision zwingt zum Neuzeichnen; die Reihen selbst aendern sich in place.
  const { channels, orientation, show, times, count, missed, dropped } = useMemo(
    () => ({
      channels: series.list(),
      orientation: hasOrientation(series.list()),
      show: series.orientationDisplay(),
      times: series.timestamps(),
      count: series.count(),
      missed: series.missedCount(),
      dropped: series.droppedCount(),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [series, revision]
  );

  // Was das Programm mit send_orientation(plot=..., view=...) verlangt hat.
  // Die Knoepfe darunter bleiben: das Programm legt den Anfang fest, wer
  // zusieht, darf trotzdem ein- und ausblenden.
  const darstellungErlaubt = orientation && (show === "both" || show === "view");
  const kurvenDerLage = show === "both" || show === "plot";
  const plotChannels = kurvenDerLage
    ? channels
    : channels.filter(
        (c) => !(ORIENTATION_CHANNELS as readonly string[]).includes(c.name)
      );

  const handleCsv = useCallback(() => {
    const blob = new Blob([toCsv(series)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "messwerte.csv";
    link.click();
    URL.revokeObjectURL(url);
  }, [series]);

  return (
    <VStack
      alignItems="stretch"
      height="100%"
      overflowY="auto"
      p="3"
      gap="2"
      bg="gray.25"
    >
      <HStack justifyContent="space-between" alignItems="center">
        <Text fontWeight="semibold">
          <FormattedMessage id="telemetry-title" />
        </Text>
        <Text fontSize="xs" color="fg.muted">
          {count} <FormattedMessage id="telemetry-samples" />
        </Text>
      </HStack>

      <HStack gap="1.5">
        <Button
          size="xs"
          variant="secondary"
          startIcon={recording ? <RiPauseLine /> : <RiPlayLine />}
          onPress={() => setRecording(!recording)}
        >
          <FormattedMessage
            id={recording ? "telemetry-pause" : "telemetry-resume"}
          />
        </Button>
        <Button
          size="xs"
          variant="secondary"
          startIcon={<RiDownloadLine />}
          onPress={handleCsv}
          isDisabled={count === 0}
        >
          CSV
        </Button>
        {darstellungErlaubt && (
          <Button
            size="xs"
            variant={showOrientation ? "primary" : "secondary"}
            startIcon={<RiBox3Line />}
            onPress={() => setShowOrientation((wert) => !wert)}
            data-testid="orientation-toggle"
          >
            <FormattedMessage id="telemetry-orientation" />
          </Button>
        )}
        <Button
          size="xs"
          variant="secondary"
          startIcon={<RiDeleteBinLine />}
          onPress={clear}
          isDisabled={count === 0}
        >
          <FormattedMessage id="telemetry-clear" />
        </Button>
      </HStack>

      {/*
        Die Spaltenbreite laesst sich ziehen, die Hoehe eines Diagramms nicht.
        Bei mehreren Kanaeln uebereinander waere jedes flach; wer einen davon
        genauer ansehen will, braucht mehr Hoehe.
      */}
      <HStack gap="1.5" alignItems="center">
        <Text fontSize="2xs" color="fg.muted">
          <FormattedMessage id="telemetry-height" />
        </Text>
        <Button
          size="xs"
          variant="secondary"
          aria-label={intl.formatMessage({ id: "telemetry-smaller" })}
          onPress={() => setHeightIndex((i) => Math.max(0, i - 1))}
          isDisabled={heightIndex === 0}
        >
          <RiZoomOutLine />
        </Button>
        <Button
          size="xs"
          variant="secondary"
          aria-label={intl.formatMessage({ id: "telemetry-larger" })}
          onPress={() =>
            setHeightIndex((i) => Math.min(HEIGHTS.length - 1, i + 1))
          }
          isDisabled={heightIndex === HEIGHTS.length - 1}
        >
          <RiZoomInLine />
        </Button>
      </HStack>

      {(missed > 0 || dropped > 0) && (
        <Text fontSize="2xs" color="fg.muted">
          {missed > 0 && (
            <FormattedMessage
              id="telemetry-missed"
              values={{ count: missed }}
            />
          )}
          {missed > 0 && dropped > 0 && " · "}
          {dropped > 0 && (
            <FormattedMessage
              id="telemetry-dropped"
              values={{ count: dropped }}
            />
          )}
        </Text>
      )}

      <RemoteControls />

      {channels.length === 0 ? (
        <Text fontSize="sm" color="fg.muted" pt="4">
          <FormattedMessage id="telemetry-empty" />
        </Text>
      ) : (
        <>
          {/*
            Die Lage steht ueber dem Diagramm, nicht an seiner Stelle: die
            Ansicht zeigt den jetzigen Stand, das Diagramm den Verlauf dorthin.
          */}
          {darstellungErlaubt && showOrientation && (
            <OrientationView series={series} height={height} />
          )}
          {/*
            Ohne Kurven kein leeres Diagramm: wer nur die Darstellung will,
            bekommt nur die Darstellung. Die Werte bleiben in der CSV-Datei.
          */}
          {plotChannels.length > 0 && (
            <>
              <TelemetryChart
                channels={plotChannels.filter((c) => !hidden.has(c.name))}
                times={times}
                height={height}
              />
              <Legend
                channels={plotChannels}
                hidden={hidden}
                onToggle={toggle}
              />
            </>
          )}
        </>
      )}
    </VStack>
  );
};

export default TelemetryPanel;
